import React, { Component } from 'react';
import { Grid, Container, Button, Typography, Skeleton } from '@mui/material';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { ChevronLeft } from '@mui/icons-material';
import { isNullOrUndefined } from 'util';
import moment from 'moment';
import { withOktaAuth } from '@okta/okta-react';
import UserContext from '../../../contexts/userContext';
import BannerLogo from '../../../assets/logo/locumtenens_com_logo_mark--white.svg';
import { COLORS } from '../../../utils/Application_Constants';

const styles = (theme) => ({
    root: {
        width: '100%',
        height: 80,
        position: 'relative',
        color: 'white',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: COLORS.DENIM,
        backgroundImage: `url(${BannerLogo})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right center',
        backgroundSize: '285px 250px',
        [theme.breakpoints.down('md')]: {
            paddingLeft: '0',
            verticalAlign: 'center',
            textAlign: 'center',
        },
    },
    pointer: {
        cursor: 'pointer',
    },
    noPadding: {
        padding: 0,
        margin: 0,
    },
    bannerButton: {
        color: 'white',
        fontSize: '16px',
    },
    alignLeft: {
        textAlign: 'left',
        [theme.breakpoints.down('md')]: {
            textAlign: 'center',
        },
    },
    alignRight: {
        textAlign: 'right',
    },
    date: {
        fontWeight: 'normal',
        [theme.breakpoints.down('md')]: {
            fontSize: '12pt',
        },
    },
    subHeading: {
        fontWeight: '300',
    },
});

class Banner extends Component {
    constructor(props, context) {
        super(props);
        this.context = context;
        this.state = {
            oktaUser: null,
            clientUser: null,
            authState: null,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (
            nextProps.clientUser !== prevState.clientUser ||
            nextProps.authState !== prevState.authState
        ) {
            if (nextProps.clientUser) {
                const { clientUser } = nextProps;
                return { ...prevState, clientUser };
            }
            if (nextProps.authState) {
                const oktaUser = nextProps.authState.idToken?.claims
                    ? nextProps.authState.idToken.claims
                    : null;
                return { ...prevState, oktaUser, authState: nextProps.authState };
            }
        }

        return null;
    }

    goBack = () => {
        this.props.history.goBack();
    };

    setTabName = () => {
        const { t } = this.props;
        const pathName = this.props.history.location.pathname;
        switch (pathName) {
            case '/profile':
            case '/profile/preference':
                return `${t('tabs:PROFILE.PREFERENCES')} - ${t(
                    'profile:PREFERENCES.ASSIGNMENT_PREFERENCES'
                )}`;
            case '/profile/sms':
                return `${t('tabs:PROFILE.PREFERENCES')} - ${t(
                    'profile:PREFERENCES.SMS_PREFERENCES'
                )}`;
            case '/profile/email':
                return `${t('tabs:PROFILE.PREFERENCES')} - ${t(
                    'profile:PREFERENCES.EMAIL_PREFERENCES'
                )}`;
            case '/profile/communication':
                return `${t('tabs:PROFILE.PREFERENCES')} - ${t(
                    'profile:PREFERENCES.COMMUNICATION_PREFERENCES'
                )}`;
            case '/profile/travel':
                return `${t('tabs:PROFILE.PREFERENCES')} - ${t(
                    'profile:PREFERENCES.TRAVEL_PREFERENCES'
                )}`;
            case '/profile/paymentElections':
                return `${t('tabs:PROFILE.PREFERENCES')} - ${t(
                    'profile:PREFERENCES.PAYMENT_ELECTION_PREFERENCES'
                )}`;
            default:
                return '';
        }
    };

    async getOktaUser() {
        const oktaUser = await this.props.oktaAuth.token.getUserInfo();
        this.setState({ ...this.state, oktaUser });
    }

    renderBannerShell(children, gridProps = {}) {
        const { classes } = this.props;
        const defaultGridProps = { className: classes.alignLeft };
        return (
            <div className={classes.root}>
                <Container maxWidth="xl">
                    <Grid container {...(gridProps.container || {})}>
                        <Grid item {...defaultGridProps} {...gridProps.item}>
                            {children}
                        </Grid>
                    </Grid>
                </Container>
            </div>
        );
    }

    render() {
        const { history, classes, pageName, t } = this.props;
        const { clientUser, oktaUser } = this.state;

        if (history.location.pathname === '/' && !clientUser && !oktaUser) {
            return null;
        }
        if (pageName === '' || pageName === 'Time and Pay') {
            if (this.props.clinicianInitialLoading) {
                return this.renderBannerShell(
                    <>
                        <Skeleton
                            variant="text"
                            width={300}
                            height={28}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}
                        />
                        <Skeleton
                            variant="text"
                            width={200}
                            height={20}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}
                        />
                    </>
                );
            }
            if (
                (isNullOrUndefined(this.context) ||
                    isNullOrUndefined(this.context.account) ||
                    isNullOrUndefined(this.context.account.idTokenClaims.given_name) ||
                    isNullOrUndefined(this.context.account.idTokenClaims.family_name)) &&
                isNullOrUndefined(clientUser) &&
                isNullOrUndefined(oktaUser) &&
                isNullOrUndefined(this.props.clinician?.displayName)
            ) {
                return this.renderBannerShell(
                    <>
                        {t('common:MESSAGES.WELCOME')}
                        <br />
                        <span className={classes.date}>{moment().format('dddd, MMMM Do')}</span>
                    </>,
                    { container: { className: classes.noPadding }, item: { className: '' } }
                );
            }

            const doctorHonorific = this.props.clinician?.useDoctorHonorific ? 'Dr. ' : '';

            const userName = clientUser
                ? `${clientUser.firstName} ${clientUser.lastName}`
                : oktaUser
                ? `${oktaUser.name}`
                : this.props.clinician?.displayName
                ? this.props.clinician.displayName
                : `${this.context.account.idTokenClaims.given_name} ${this.context.account.idTokenClaims.family_name}`;

            return this.renderBannerShell(
                <>
                    <Typography variant="body1">
                        {`${t('common:MESSAGES.WELCOME')},
                            ${doctorHonorific}${userName}
                        `}
                    </Typography>
                    <Typography variant="body1" className={classes.subHeading}>
                        {moment().format('dddd, MMMM Do')}
                    </Typography>
                </>
            );
        }
        return (
            <div className={classes.root}>
                <Container maxWidth="xl">
                    <Grid container>
                        <Grid item>
                            <Button
                                className={classes.bannerButton}
                                onClick={this.goBack}
                                variant="text"
                                color="primary"
                                startIcon={<ChevronLeft />}
                            >
                                {pageName === 'Profile' ? this.setTabName() : pageName}
                            </Button>
                        </Grid>
                    </Grid>
                </Container>
            </div>
        );
    }
}

Banner.contextType = UserContext;

Banner.propTypes = {
    classes: PropTypes.shape({
        root: PropTypes.string.isRequired,
        backgroundColor: PropTypes.string,
        image: PropTypes.string,
    }).isRequired,
    pageName: PropTypes.string.isRequired,
};

export default withRouter(withTranslation()(withStyles(styles)(withOktaAuth(Banner))));
